/* Read the existing Color chart and select the first available matching APO option. */
(() => {
  'use strict';
  if (window.bsColorChartsLoaded || !window.HTMLDialogElement) return;
  window.bsColorChartsLoaded = true;

  const triggerSelector = '.size-chart__colors a[href]';
  const catalogCache = new Map();
  let active = null;
  let serial = 0;
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  const normalize = value => clean(value).toUpperCase();
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  function imageURL(raw, base) {
    if (!raw) return '';
    try {
      const url = new URL(raw, base);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  }

  function parseCatalog(html, url) {
    const page = new DOMParser().parseFromString(html, 'text/html');
    const groups = [];
    const codes = new Set();
    page.querySelectorAll('.colorTable').forEach(section => {
      const cards = [];
      section.querySelectorAll('.color_block').forEach(card => {
        const code = clean(card.querySelector('.color_block__material')?.textContent);
        if (!code) return;
        const picture = card.querySelector('.color_block__img');
        const background = picture?.style.backgroundImage || '';
        const match = background.match(/^url\(["']?(.*?)["']?\)$/);
        const image = imageURL(match?.[1] || picture?.querySelector('img')?.getAttribute('src') || '', url);
        cards.push({ code, key: normalize(code), name: clean(card.querySelector('.color_block__color')?.textContent), image });
        codes.add(normalize(code));
      });
      if (cards.length) groups.push({
        title: clean(section.querySelector('.SectionHeader__SubHeading, .colorTable_block__header, .colorTable_header')?.textContent),
        cards
      });
    });
    if (!groups.length) throw new Error('Color chart is unavailable.');
    return { groups, codes, note: clean(page.querySelector('.color-chart-note')?.textContent) };
  }

  function catalog(url) {
    if (!catalogCache.has(url)) {
      const request = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        try {
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
          if (!response.ok) throw new Error('Color chart is unavailable.');
          return parseCatalog(await response.text(), url);
        } finally { clearTimeout(timeout); }
      })();
      catalogCache.set(url, request);
      request.catch(() => catalogCache.delete(url));
    }
    return catalogCache.get(url);
  }

  function availableColors(form, data) {
    const colors = new Map();
    let ready = false;
    form.querySelectorAll('select').forEach(select => {
      if (select.disabled) return;
      const field = select.closest('.mw-option') || select.parentElement;
      // APO can hide whole fields when conditional options change.
      for (let node = field; node && node !== form; node = node.parentElement) {
        if (node.hidden || getComputedStyle(node).display === 'none') return;
      }
      const label = clean(field?.querySelector('label')?.textContent || select.labels?.[0]?.textContent || select.name);
      const colorField = /\bcolou?rs?\b/i.test(label);
      const values = [];
      Array.from(select.options).forEach(option => {
        if (option.disabled || option.hidden || !option.value) return;
        const text = clean(option.textContent);
        // Match the whole code, so 7040 never matches mesh code 70401.
        const prefix = text.match(/^([a-z0-9][a-z0-9._/]*)\s*[-–—:]/i);
        const key = normalize(prefix ? prefix[1] : text);
        if (data.codes.has(key) || (colorField && prefix && /\d/.test(key))) {
          values.push({ key, code: prefix ? prefix[1] : text, name: text, select, option });
        }
      });
      if (colorField || values.length) {
        if (select.options.length > 1) ready = true;
        values.forEach(value => {
          if (!colors.has(value.key)) colors.set(value.key, value);
        });
      }
    });
    return { colors, ready };
  }

  function makeDialog(className, title) {
    const dialog = element('dialog', `bscc-dialog ${className}`);
    const id = `bscc-title-${++serial}`;
    dialog.setAttribute('aria-labelledby', id);
    const header = element('header', 'bscc-header');
    const heading = element('h2', 'bscc-title Heading', title);
    heading.id = id;
    const close = element('button', 'bscc-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', () => dialog.close());
    header.append(heading, close);
    dialog.append(header);
    let outside = false;
    const isOutside = event => {
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    };
    dialog.addEventListener('pointerdown', event => { outside = event.target === dialog && isOutside(event); });
    dialog.addEventListener('click', event => {
      if (outside && event.target === dialog && isOutside(event)) dialog.close();
      outside = false;
    });
    return { dialog, header };
  }

  function chooseColor(card, state) {
    // Re-read at click time: APO may have changed options since the window opened.
    const selection = availableColors(state.form, state.data);
    const match = selection.colors.get(card.key);
    if (!match) {
      render(state, selection);
      return;
    }
    // Keep DOM order, including the first available thickness and first color field.
    const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set;
    setter.call(match.select, match.option.value);
    match.select.dispatchEvent(new Event('input', { bubbles: true }));
    match.select.dispatchEvent(new Event('change', { bubbles: true }));
    state.dialog.close();
  }

  function render(state, selection) {
    const fragment = document.createDocumentFragment();
    const seen = new Set();
    const missing = new Map(selection.colors);
    state.data.groups.forEach(group => {
      const cards = group.cards.filter(card => selection.colors.has(card.key) && !seen.has(card.key));
      if (!cards.length) return;
      const section = element('section', 'colorTable');
      const block = element('div', 'colorTable_block');
      if (group.title) block.append(element('h4', 'SectionHeader__SubHeading Heading u-h4', group.title));
      const grid = element('div', 'colorTable_block__blocks');
      cards.forEach(card => {
        if (seen.has(card.key)) return;
        seen.add(card.key);
        if (!card.image) return;
        missing.delete(card.key);
        const link = element('button', 'color_block bscc-color-choice');
        link.type = 'button';
        link.setAttribute('aria-label', `Select ${card.code} — ${card.name}`);
        const wrapper = element('div', 'color_block__wrapper');
        const picture = element('div', 'color_block__img');
        picture.style.background = `url(${JSON.stringify(card.image)}) center/cover no-repeat`;
        wrapper.append(picture, element('span', 'color_block__material', card.code), element('span', 'color_block__color', card.name));
        link.append(wrapper);
        link.addEventListener('click', () => chooseColor(card, state));
        grid.append(link);
      });
      if (grid.children.length) {
        block.append(grid);
        section.append(block);
        fragment.append(section);
      }
    });
    if (missing.size) {
      const section = element('section', 'bscc-missing');
      section.append(element('h4', 'Heading u-h4', 'Samples not yet available'));
      const list = element('ul');
      missing.forEach(color => list.append(element('li', '', color.name)));
      section.append(list);
      fragment.append(section);
    }
    if (!selection.colors.size) fragment.append(element('p', 'bscc-status', 'No color options are available. You can browse the full color chart below.'));
    state.content.replaceChildren(fragment);
    state.content.removeAttribute('aria-busy');
  }

  function watchOptions(state) {
    let timer;
    let signature = '';
    const update = () => {
      if (!state.dialog.open) return;
      const selection = availableColors(state.form, state.data);
      if (!selection.ready && !signature) return;
      const next = JSON.stringify(Array.from(selection.colors, ([key, value]) => [key, value.name, value.option.value, value.option.index]));
      if (signature !== next) {
        signature = next;
        render(state, selection);
      }
      clearTimeout(state.waitTimeout);
    };
    const schedule = () => { clearTimeout(timer); timer = setTimeout(update, 100); };
    const observer = new MutationObserver(schedule);
    observer.observe(state.form, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'style', 'class', 'value'] });
    state.form.addEventListener('change', schedule);
    state.cleanup = () => {
      observer.disconnect();
      clearTimeout(timer);
      clearTimeout(state.waitTimeout);
      state.form.removeEventListener('change', schedule);
    };
    state.waitTimeout = setTimeout(() => {
      if (!state.dialog.open || signature) return;
      state.content.removeAttribute('aria-busy');
      state.content.replaceChildren(element('p', 'bscc-status', 'Color options have not loaded. Please try again or open the full color chart below.'));
    }, 12000);
    update();
  }

  async function open(trigger) {
    if (active) return;
    const form = trigger.closest('form');
    if (!form) return;
    const { dialog, header } = makeDialog('', 'Color guide');
    const title = clean(form.querySelector('h1')?.textContent || document.querySelector('.ProductMeta__Title')?.textContent);
    if (title) header.append(element('p', 'bscc-product', title));
    const content = element('div', 'bscc-content');
    content.setAttribute('aria-busy', 'true');
    const status = element('p', 'bscc-status', 'Loading available colors…');
    status.setAttribute('role', 'status');
    content.append(status);
    const footer = element('footer', 'bscc-footer');
    const all = element('a', 'bscc-all', 'View all colors');
    all.href = trigger.href;
    footer.append(all);
    dialog.append(content, footer);
    const state = { dialog, form, content, cleanup: () => {} };
    active = state;
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    trigger.setAttribute('aria-expanded', 'true');
    dialog.addEventListener('close', () => {
      state.cleanup();
      dialog.remove();
      document.documentElement.style.overflow = previousOverflow;
      trigger.setAttribute('aria-expanded', 'false');
      active = null;
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
    }, { once: true });
    document.body.append(dialog);
    dialog.showModal();
    try {
      state.data = await catalog(trigger.href);
      if (!dialog.open) return;
      if (state.data.note) footer.prepend(element('p', 'bscc-note', state.data.note));
      watchOptions(state);
    } catch (_) {
      if (!dialog.open) return;
      content.removeAttribute('aria-busy');
      content.replaceChildren(element('p', 'bscc-status', 'Unable to load the color samples. Please try again or open the full color chart below.'));
    }
  }

  document.querySelectorAll(triggerSelector).forEach(trigger => {
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');
    if (!trigger.hasAttribute('aria-label')) trigger.setAttribute('aria-label', 'Color guide');
  });
  document.addEventListener('click', event => {
    const trigger = event.target.closest?.(triggerSelector);
    if (!trigger || !trigger.closest('form') || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    open(trigger);
  });
})();
